/**
 * PublicPitch — no-auth landing page for a single shared property.
 *
 * Loaded at `/pitch/:slug`. Fires a view beacon on mount, renders a
 * premium clean-white deal card, and captures inquiries into
 * `deal_share_inquiries` via POST /api/public/deal/:slug/inquiry.
 *
 * Design language: Clean & Premium White — crisp white background,
 * generous whitespace, emerald accent, big black tabular numbers.
 * (Airbnb / Notion aesthetic.)
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, BedDouble, Bath, Square, Calendar,
  DollarSign, Target, Loader2, CheckCircle2,
  ArrowRight, Share2, Shield,
} from "lucide-react";
import { API, fmtMoney } from "@/lib/api";

function StatBlock({ label, value, sub, accent, isAccent = false }) {
  return (
    <div className="flex flex-col gap-1" data-testid={`pitch-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</div>
      <div
        className="font-display font-black text-3xl md:text-4xl tabular-nums leading-none tracking-tighter"
        style={{ color: isAccent ? accent : "#0F172A" }}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function InfoPill({ icon: Icon, children }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-sm text-slate-700 shadow-sm">
      <Icon size={13} className="text-slate-500" />
      {children}
    </div>
  );
}

export default function PublicPitch() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", cash_ready: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/public/deal/${slug}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => {
        if (alive) setError(e?.response?.status === 404
          ? "This deal is no longer available or the link has been retired."
          : "Something went wrong loading this deal.");
      })
      .finally(() => { if (alive) setLoading(false); });

    axios.post(`${API}/public/deal/${slug}/track`, {
      referer: document.referrer || "",
    }).catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Name, email, and phone are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/deal/${slug}/inquiry`, form);
      setSubmitted(true);
      toast.success("Your interest has been sent to the wholesaler.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit. Try calling instead.");
    } finally {
      setSubmitting(false);
    }
  }, [slug, form]);

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      toast.success("Link copied — paste anywhere.");
    }).catch(() => toast.error("Copy failed. Long-press the URL bar."));
  }, []);

  const property = data?.property;
  const owner = property?.owner;
  const accent = owner?.accent || "#10B981";

  const mao = useMemo(() => {
    if (!property?.estimated_arv) return null;
    const arv = Number(property.estimated_arv);
    const repairs = Number(property.estimated_repairs || 0);
    if (Number.isNaN(arv)) return null;
    return Math.round(arv * 0.7 - repairs);
  }, [property]);

  const spread = useMemo(() => {
    if (!property?.asking_price || !mao) return null;
    return mao - Number(property.asking_price);
  }, [property, mao]);

  if (loading) {
    return (
      <div className="min-h-screen exos flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading deal…</span>
        </div>
      </div>
    );
  }
  if (error || !property) {
    return (
      <div className="min-h-screen exos flex items-center justify-center px-8">
        <div className="max-w-md text-center space-y-3">
          <div className="text-4xl">🚫</div>
          <div className="text-lg font-semibold text-slate-900">Deal unavailable</div>
          <div className="text-sm text-slate-600">{error}</div>
          <a href="/" className="inline-block mt-4 text-sm text-slate-600 hover:text-emerald-600 underline underline-offset-4">← Back to iSpeedToClose</a>
        </div>
      </div>
    );
  }

  const addr = property.address || {};
  const fullAddress = [addr.street, addr.city, addr.state, addr.zip_code].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen exos exos-noise" data-testid="public-pitch-page">
      {/* Header */}
      <header className="border-b border-slate-200/70 exos-glass sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {owner?.logo_url ? (
              <img src={owner.logo_url} alt="" className="h-8 w-auto" data-testid="pitch-owner-logo" />
            ) : (
              <div
                className="h-8 w-8 rounded flex items-center justify-center font-bold text-sm text-slate-900"
                style={{ background: accent }}
              >
                {(owner?.name || "W").charAt(0)}
              </div>
            )}
            <div className="leading-tight">
              <div className="text-sm font-semibold text-slate-900" data-testid="pitch-owner-name">{owner?.company || owner?.name || "Wholesaler"}</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-widest">Exclusive Cash Buyer Deal</div>
            </div>
          </div>
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-full border border-slate-200 bg-white/60 hover:border-emerald-500 text-slate-700 transition-colors"
            data-testid="pitch-copy-link"
          >
            <Share2 size={12} />
            <span className="hidden sm:inline">Share this deal</span>
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-24 z-10">
        <span className="exos-eyebrow">
          Property · {property.stage || "under contract"}
        </span>
        <h1
          className="font-display font-black text-5xl md:text-7xl leading-[0.98] tracking-tighter text-slate-900 mt-5"
          data-testid="pitch-address"
        >
          {addr.street}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-slate-600 text-sm md:text-base">
          <MapPin size={15} className="text-slate-500" />
          <span>{[addr.city, addr.state, addr.zip_code].filter(Boolean).join(", ")}</span>
        </div>

        {/* Big-number bar */}
        <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 border-y border-slate-200 py-10 md:py-12">
          <StatBlock
            label="Cash Buyer Price"
            value={property.asking_price != null ? fmtMoney(property.asking_price) : "—"}
            sub="Assignment fee included"
            isAccent
            accent={accent}
          />
          <StatBlock
            label="Estimated ARV"
            value={property.estimated_arv != null ? fmtMoney(property.estimated_arv) : "—"}
            sub="After repair value"
          />
          <StatBlock
            label="Rehab Estimate"
            value={property.estimated_repairs != null ? fmtMoney(property.estimated_repairs) : "—"}
            sub="Cosmetic + light structural"
          />
          <StatBlock
            label="Max Allowable Offer"
            value={mao != null ? fmtMoney(mao) : "—"}
            sub="70% rule (ARV × 0.7 − repairs)"
          />
        </div>

        {/* Info pills */}
        <div className="mt-10 flex flex-wrap gap-2">
          {property.bedrooms != null && <InfoPill icon={BedDouble}>{property.bedrooms} bed</InfoPill>}
          {property.bathrooms != null && <InfoPill icon={Bath}>{property.bathrooms} bath</InfoPill>}
          {property.sqft != null && <InfoPill icon={Square}>{property.sqft.toLocaleString()} sqft</InfoPill>}
          {property.year_built != null && <InfoPill icon={Calendar}>Built {property.year_built}</InfoPill>}
          {property.property_type && <InfoPill icon={Target}>{property.property_type}</InfoPill>}
          {property.current_deal_score != null && (
            <InfoPill icon={CheckCircle2}>Deal Score {property.current_deal_score}/100</InfoPill>
          )}
        </div>

        {/* Spread callout */}
        {spread != null && spread > 0 && (
          <div
            className="mt-12 inline-flex items-center gap-4 px-6 py-4 rounded-xl border"
            style={{ borderColor: `${accent}55`, background: `${accent}12`, boxShadow: `0 0 40px -12px ${accent}` }}
          >
            <DollarSign size={22} style={{ color: accent }} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: accent }}>Buyer Spread</div>
              <div className="font-display font-black text-3xl tabular-nums tracking-tighter" style={{ color: accent }}>
                +{fmtMoney(spread)}
              </div>
            </div>
          </div>
        )}

        {/* Deal thesis */}
        {property.notes && (
          <div className="mt-14 max-w-3xl">
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-600 mb-3 font-bold">Why This Deal Works</div>
            <div className="text-slate-700 text-lg leading-relaxed whitespace-pre-line" data-testid="pitch-thesis">
              {property.notes}
            </div>
          </div>
        )}
      </section>

      {/* Inquiry form */}
      <section className="relative max-w-6xl mx-auto px-5 md:px-8 pb-24 z-10">
        <div className="rounded-2xl exos-card p-8 md:p-12 shadow-sm"
             data-testid="pitch-inquiry-panel">
          {submitted ? (
            <div className="text-center py-8" data-testid="pitch-submitted">
              <CheckCircle2 size={40} className="mx-auto mb-4" style={{ color: accent }} />
              <div className="text-2xl font-display font-black mb-2 text-slate-900">You&apos;re on the list.</div>
              <div className="text-slate-600 max-w-md mx-auto">
                {owner?.name || "The wholesaler"} will reach out shortly. First serious offer typically wins — don&apos;t wait for a callback.
              </div>
            </div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-600 mb-2 font-bold">Reserve this deal</div>
              <div className="font-display font-black text-2xl md:text-3xl mb-2 text-slate-900 tracking-tighter">
                Want first look? Drop your info.
              </div>
              <div className="text-sm text-slate-600 mb-6 flex items-center gap-1.5">
                <Shield size={12} /> Your info is only shared with the wholesaler.
              </div>
              <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="px-4 py-3 rounded-lg bg-white border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 placeholder:text-slate-500 transition-colors"
                  data-testid="pitch-input-name"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="px-4 py-3 rounded-lg bg-white border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 placeholder:text-slate-500 transition-colors"
                  data-testid="pitch-input-email"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                  className="px-4 py-3 rounded-lg bg-white border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 placeholder:text-slate-500 transition-colors"
                  data-testid="pitch-input-phone"
                />
                <input
                  type="text"
                  placeholder="Cash ready (e.g. $75k)"
                  value={form.cash_ready}
                  onChange={(e) => setForm({ ...form, cash_ready: e.target.value })}
                  className="px-4 py-3 rounded-lg bg-white border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 placeholder:text-slate-500 transition-colors"
                  data-testid="pitch-input-cash"
                />
                <textarea
                  placeholder="Optional: what you'd offer or timeline..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  className="md:col-span-2 px-4 py-3 rounded-lg bg-white border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-slate-900 placeholder:text-slate-500 resize-none transition-colors"
                  data-testid="pitch-input-message"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="md:col-span-2 flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-bold text-slate-900 transition-transform active:scale-[0.98] hover:scale-[1.01] disabled:opacity-60 shadow-lg"
                  style={{ background: accent, boxShadow: `0 12px 32px -8px ${accent}55` }}
                  data-testid="pitch-submit"
                >
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin" /> Sending…</>
                  ) : (
                    <>Reserve This Deal <ArrowRight size={16} /></>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </section>

      {/* Footer with acquisition loop */}
      <footer className="border-t border-slate-200 px-5 md:px-8 py-8 max-w-6xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div>Sent by <span className="text-slate-700 font-medium">{owner?.name || "a wholesaler"}</span> · {fullAddress}</div>
          <a href="/" className="inline-flex items-center gap-2 hover:text-emerald-600 transition-colors" data-testid="pitch-footer-cta">
            Powered by iSpeedToClose <ArrowRight size={12} />
          </a>
        </div>
      </footer>
    </div>
  );
}
